// eslint-disable-next-line jira/filename-case
import React, { createContext, useMemo, type PropsWithChildren } from 'react';
import { tenantHasActiveProductDiscoveryLicenseSelector } from '@jira/issue__issue-view-store/src/issueKeySelector.tsx';
import { useUserHasAddProductPermissions } from '@jira/issue__placeholder-template/src/useUserHasAddProductPermissions.tsx';
import type { LoomTouchpointVariantType } from '@jira/growth__loom-utils/src/controllers/use-loom-touchpoint-variant/types.tsx';
import { useIsOrgAdmin } from '@jira/business__shared-components-goals-picker/src/services/is-org-admin/index.tsx';
import { usePreviousPersistent } from '@jira/platform__utils-react-hooks-use-previous/src/common/utils/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useSelector } from '@atlassian/jira-react-redux/src/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useAreProductSuggestionsEnabled } from '@atlassian/jira-tenant-context-controller/src/components/are-product-suggestions-enabled/index.tsx';

type CrossFlowPostOfficeContextType = {
	loomTouchpointVariant: LoomTouchpointVariantType | null;
	isAdmin: boolean | null;
	isOrgAdmin: boolean | null;
	hasActiveJpdLicense: boolean | null;
	tenantHasConfluence: boolean | null;
	canProvisionConfluenceEndUser: boolean | null;
	areProductSuggestionsEnabled: boolean | null;
	isAiEligibleAndTriggered?: boolean | null;
};

export const CrossFlowPostOfficeContext: React.Context<CrossFlowPostOfficeContextType> =
	createContext<CrossFlowPostOfficeContextType>({
		loomTouchpointVariant: null,
		isAdmin: null,
		isOrgAdmin: null,
		hasActiveJpdLicense: null,
		tenantHasConfluence: null,
		canProvisionConfluenceEndUser: null,
		areProductSuggestionsEnabled: null,
		isAiEligibleAndTriggered: null,
	});

type CrossFlowPostOfficeProviderProps = PropsWithChildren<{
	// loomTouchpointVariant is being passed down from props and not being computed here because
	// 1. it is alredy available in the parent component
	// 2. if we fetch it here, it makes an api call and thus re renders the component
	loomTouchpointVariant: LoomTouchpointVariantType | null;
	/* Passing information about if AI banner is eligible and triggered */
	isAiEligibleAndTriggered?: boolean | null;
}>;

// This component should never re render more than once as every rerender will cause post office placement to re render
export const CrossFlowPostOfficeProvider = ({
	children,
	loomTouchpointVariant,
	isAiEligibleAndTriggered,
}: CrossFlowPostOfficeProviderProps): React.JSX.Element => {
	const licensedProducts = useLicensedProducts();
	const tenantHasConfluence = licensedProducts?.confluence === true;
	const isSiteAdmin = useIsSiteAdmin();
	const { canProvisionConfluenceEndUser } = useUserHasAddProductPermissions({
		skip: isSiteAdmin || tenantHasConfluence,
	});

	const areProductSuggestionsEnabled = useAreProductSuggestionsEnabled();

	const isAdmin = useIsAdmin();
	const hasActiveJpdLicense: boolean = useSelector(tenantHasActiveProductDiscoveryLicenseSelector);

	// isOrgAdmin is fetched from an api call and the initial value of loading is false
	// so it changes from false to true and then back to false
	// we need to wait for the loading to be true and then false again before we can render the children
	// this is to avoid re render of post office placement
	// to achieve this, we use usePrevious hook to check the previous value of isOrgAdminLoading
	// and only render the children when the previous value is true and the current value is false
	const { isOrgAdmin, loading: isOrgAdminLoading } = useIsOrgAdmin();
	const previousIsOrgAdminLoading = usePreviousPersistent(isOrgAdminLoading);

	const context = useMemo(() => {
		return {
			loomTouchpointVariant,
			isAdmin,
			isOrgAdmin,
			hasActiveJpdLicense,
			tenantHasConfluence,
			canProvisionConfluenceEndUser,
			areProductSuggestionsEnabled,
			isAiEligibleAndTriggered: !!isAiEligibleAndTriggered,
		};
	}, [
		loomTouchpointVariant,
		isAdmin,
		isOrgAdmin,
		hasActiveJpdLicense,
		tenantHasConfluence,
		canProvisionConfluenceEndUser,
		areProductSuggestionsEnabled,
		isAiEligibleAndTriggered,
	]);

	return (
		<CrossFlowPostOfficeContext.Provider value={context}>
			{previousIsOrgAdminLoading && !isOrgAdminLoading && children}
		</CrossFlowPostOfficeContext.Provider>
	);
};
